import { dinero, toDecimal, toUnits, add, type Dinero, type DineroCurrency } from 'dinero.js';
import getSymbolFromCurrency from 'currency-symbol-map';
import cc from 'currency-codes';

import { dineroCurrencyMapping } from 'lib/dinero-mapping';
import { getApprovedCurrencyCodes } from 'constants/approvedCurrencies';

/**
 * @param amount - Amount in cents, e.g. 50000
 * @param dineroCurrency - dinero currency object, e.g. { code: 'USD', base: 100, exponent: 2 }
 * @returns string, e.g. 500.00
 * @see https://dinerojs.com/docs/dinero
 */
/**
 * dinero's `toDecimal` throws "Currency is not decimal" on any currency whose base is not 10.
 * MGA and MRU are base 5, and MGA is approved for contracts, so this has to be checked rather
 * than assumed.
 */
const isDecimalCurrency = (currency: DineroCurrency<number>) =>
  Array.isArray(currency.base) ? currency.base.every((base) => base === 10) : currency.base === 10;

/**
 * Minor units per major unit. Not 10^exponent: MGA and MRU are base five, so their ariary and
 * ouguiya divide by 5, not 10. Everything that turns a minor-unit integer into a major-unit value
 * reads this, so a chart's bars and its tooltips cannot end up on different scales.
 */
const getCurrencyScale = (currency: DineroCurrency<number>) => {
  const { base, exponent } = currency;
  if (typeof base !== 'number') return Math.pow(10, exponent);
  return Math.pow(base, exponent);
};

/**
 * Render a dinero amount at its currency's own exponent: "1234.56" for USD, "1234" for JPY,
 * "1.234" for BHD, "1.4" for MGA.
 *
 * Non-decimal currencies cannot go through `toDecimal`, but their fraction is still exact in
 * decimal: MGA and MRU divide by five, so a remainder is always some multiple of 0.2. Reading only
 * the whole units would round a 2 subunit amount down to 0 and drop the sign off a negative one.
 */
const formatDineroAmount = (d: Dinero<number>, dineroCurrency: DineroCurrency<number>) => {
  if (isDecimalCurrency(dineroCurrency)) return toDecimal(d, ({ value }) => `${value}`);

  const [whole, remainder = 0] = toUnits(d);
  if (!remainder) return `${whole}`;

  // No currency in the mapping has an array base, and one could not be scaled by a single power.
  if (typeof dineroCurrency.base !== 'number') return `${whole}`;

  const scale = getCurrencyScale(dineroCurrency);
  const decimals = Math.max(0, Math.ceil(Math.log10(scale)));
  return (whole + remainder / scale).toFixed(decimals);
};

export const getCurrencyDisplayString = (amount: number, dineroCurrency: DineroCurrency<number>) => {
  const d = dinero({ amount: amount, currency: dineroCurrency });

  return formatDineroAmount(d, dineroCurrency);
};

/**
 * @param currencyCode - Currency code, e.g. USD, EUR, GBP, etc.
 * @returns Currency symbol, e.g. $, €, £, etc.
 */
export const getCurrencySymbol = (currencyCode: string) => {
  if (!currencyCode) return getSymbolFromCurrency('USD');
  return getSymbolFromCurrency(currencyCode.toUpperCase()); // $, €, £, ¥, etc.
};

/**
 * @param currencyCode - Currency code, e.g. USD, EUR, GBP, etc.
 * @returns Dinero currency object, e.g. { code: 'USD', base: 100, exponent: 2 }
 * @see https://dinerojs.com/docs/currencies
 */
export const getDineroCurrency = (currencyCode: string): DineroCurrency<number> => {
  const dineroCurrency = dineroCurrencyMapping[currencyCode?.toUpperCase()];
  if (!dineroCurrency) {
    console.warn(`Unsupported currency code: ${currencyCode}, falling back to USD`);
    return dineroCurrencyMapping['USD'];
  }
  return dineroCurrency;
};

/**
 * @param amount - Amount in cents, e.g. 50000
 * @param currencyCode - Currency code, e.g. USD, EUR, GBP, etc.
 * @returns string, e.g. $500.00
 */
export const displayCurrencyWithSymbol = (amount: number, currencyCode: string, withSymbol = true) => {
  const dineroCurrency = getDineroCurrency(currencyCode);
  const currencySymbol = getCurrencySymbol(currencyCode);
  const currencyDisplayString = getCurrencyDisplayString(amount, dineroCurrency);

  if (!withSymbol) {
    return currencyDisplayString;
  }

  return `${currencySymbol}${currencyDisplayString}`;
};

export const addAmounts = (amounts: number[], currencyCode = 'USD') => {
  const dineroCurrency = getDineroCurrency(currencyCode);

  const d = amounts.reduce(
    (acc, amount) => {
      return add(acc, dinero({ amount: amount, currency: dineroCurrency }));
    },
    dinero({ amount: 0, currency: dineroCurrency }),
  );

  const currencySymbol = getCurrencySymbol(currencyCode);
  const amount = formatDineroAmount(d, dineroCurrency);

  return `${currencySymbol}${amount}`;
};

export function centsToDollars(cents: number): number {
  return parseFloat((cents / 100).toFixed(2));
}

export function dollarsToCents(dollars: number): number {
  const dollarsString = dollars.toString();
  return Math.round(parseFloat(dollarsString) * 100);
}

export const currenciesDropdown = (() => {
  const approvedCurrencyCodes = getApprovedCurrencyCodes();
  return Object.keys(dineroCurrencyMapping)
    .filter((code) => approvedCurrencyCodes.includes(code))
    .map((code) => {
      return { id: cc.code(code).currency, value: code, label: code, additionalValue: cc.code(code).currency };
    });
})();

/**
 * Short label for a currency code, e.g. "USD" becomes "USD ($)". Lives here so a code never
 * renders bare in one place and decorated in another.
 */
export const formatCurrencyCodeLabel = (currencyCode: string): string => {
  if (!currencyCode) return '';
  const code = currencyCode.toUpperCase();
  const symbol = getCurrencySymbol(code);
  return symbol && symbol !== code ? `${code} (${symbol})` : code;
};

/**
 * Full currency name for a code, e.g. "USD" becomes "US Dollar". Falls back to the code itself
 * for anything `currency-codes` does not know.
 */
export const getCurrencyName = (currencyCode: string): string => {
  if (!currencyCode) return '';
  const code = currencyCode.toUpperCase();
  return cc.code(code)?.currency || code;
};

/**
 * Minor units to major units using the currency's own exponent. 1000 JPY minor units is 1000 yen,
 * not 10: only two-decimal currencies divide by 100. Chart axes must convert through this, because
 * the tooltip beside them formats through the same exponent and would otherwise disagree.
 */
export function subunitsToCurrency(amount: number, currencyCode: string): number {
  return amount / getCurrencyScale(getDineroCurrency(currencyCode));
}

export function convertCurrencyToSubunits(amount: number, exponent = 2): number {
  // Calculate the multiplier using 10 raised to the power of the exponent
  const multiplier = Math.pow(10, exponent);

  // Convert the amount to subunits (multiply by the multiplier)
  const subunitsAmount = amount * multiplier;

  return subunitsAmount;
}
